import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "./ui/alert-dialog";
import { api, formatApiErrorDetail, formatMXN } from "../lib/api";
import { toast } from "sonner";
import { Archive, Trash2 } from "lucide-react";

export default function EditAccountDialog({ open, onOpenChange, account, onChanged }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("efectivo");
  const [saving, setSaving] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // "archive" | "delete" | null
  const [actionRunning, setActionRunning] = useState(false);

  useEffect(() => {
    if (!account) return;
    setName(account.name);
    setType(account.type);
  }, [account]);

  // Reset internal state when the dialog closes (prevents stale modals on next open)
  useEffect(() => {
    if (!open) {
      setConfirmAction(null);
      setActionRunning(false);
    }
  }, [open]);

  if (!account) return null;
  const balance = Number(account.balance) || 0;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/accounts/${account.id}`, { name, type });
      toast.success("Cuenta actualizada");
      onOpenChange(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const requestArchive = () => {
    if (balance !== 0) {
      toast.error("Transfiere primero el saldo a otra cuenta (debe quedar en 0)");
      return;
    }
    setConfirmAction("archive");
  };

  const requestDelete = () => setConfirmAction("delete");

  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    setActionRunning(true);
    try {
      if (confirmAction === "archive") {
        await api.patch(`/accounts/${account.id}`, { archived: true });
        toast.success("Cuenta archivada");
      } else {
        await api.delete(`/accounts/${account.id}`);
        toast.success("Cuenta eliminada");
      }
      setConfirmAction(null);
      onOpenChange(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Operación no permitida");
      setConfirmAction(null); // close the confirm sheet but keep the edit dialog open
    } finally {
      setActionRunning(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm bg-bone border-line rounded-3xl" data-testid="edit-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Editar cuenta</DialogTitle>
            <DialogDescription className="text-soft text-sm">
              Saldo actual: <span className="font-bold tabular">{formatMXN(balance)}</span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-1">
                Nombre
              </label>
              <input
                data-testid="edit-account-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-white border border-line rounded-xl px-3 py-3 outline-none focus:border-sage-500 text-ink"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-2">
                Tipo
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "efectivo", label: "Efectivo" },
                  { key: "banco", label: "Banco" },
                  { key: "tarjeta", label: "Tarjeta" },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setType(t.key)}
                    className={`py-2.5 rounded-xl text-sm font-bold border transition-colors ${
                      type === t.key
                        ? "bg-sage-900 text-bone border-sage-900"
                        : "bg-white border-line text-ink"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="edit-account-save"
              className="w-full bg-sage-900 text-bone rounded-2xl py-3 font-bold hover:bg-sage-900/90 disabled:opacity-60"
            >
              {saving ? "Guardando…" : "Guardar cambios"}
            </button>
          </form>

          <div className="mt-4 space-y-2">
            <button
              type="button"
              onClick={requestArchive}
              data-testid="edit-account-archive"
              className="w-full inline-flex items-center justify-center gap-2 bg-white border border-line text-ink font-bold py-3 rounded-2xl hover:bg-sage-50"
            >
              <Archive size={14} /> Archivar cuenta
            </button>
            <button
              type="button"
              onClick={requestDelete}
              data-testid="edit-account-delete"
              className="w-full inline-flex items-center justify-center gap-2 bg-clay/10 text-clay font-bold py-3 rounded-2xl hover:bg-clay/15"
            >
              <Trash2 size={14} /> Eliminar definitivamente
            </button>
            <p className="text-[11px] text-soft text-center leading-relaxed pt-1">
              Archivar requiere saldo 0 · Eliminar solo si la cuenta no tiene movimientos
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmAction !== null}
        onOpenChange={(o) => { if (!o && !actionRunning) setConfirmAction(null); }}
      >
        <AlertDialogContent
          className="max-w-sm bg-bone border-line rounded-3xl"
          data-testid="confirm-account-action-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">
              {confirmAction === "delete"
                ? `¿Eliminar "${account.name}"?`
                : `¿Archivar "${account.name}"?`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-soft text-sm leading-relaxed">
              {confirmAction === "delete"
                ? "Solo funciona si la cuenta no tiene movimientos. Esta acción no se puede deshacer."
                : "Dejará de aparecer en selectores y resúmenes, pero conservaremos su historial."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2 mt-2">
            <AlertDialogCancel
              disabled={actionRunning}
              data-testid="confirm-account-cancel"
              className="bg-white border-line text-ink rounded-2xl font-bold"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runConfirmedAction(); }}
              disabled={actionRunning}
              data-testid="confirm-account-confirm"
              className={`rounded-2xl font-bold ${
                confirmAction === "delete"
                  ? "bg-clay text-bone hover:bg-clay/90"
                  : "bg-sage-900 text-bone hover:bg-sage-900/90"
              }`}
            >
              {actionRunning
                ? "Procesando…"
                : confirmAction === "delete" ? "Sí, eliminar" : "Sí, archivar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
